'use client';

import { MediaSlot } from '@/components/ui/MediaSlot';
import { Skeleton } from '@/components/ui/skeleton';
import {
  type MarketingAssetKey,
  marketingAssets,
} from '@/lib/marketing/assets';
import { theme } from '@/lib/ui/theme';

interface Shot {
  assetKey: MarketingAssetKey;
  caption: string;
  hidden?: boolean;
}

interface FeatureShotsSectionProps {
  title: string;
  lead: string;
  shots: Shot[];
  /** Optional line above the title, introducing a group of sibling sections. */
  intro?: string;
  isLoading?: boolean;
}

export function FeatureShotsSection({
  title,
  lead,
  shots,
  intro,
  isLoading = false,
}: FeatureShotsSectionProps) {
  const visibleShots = shots.filter((shot) => !shot.hidden);

  if (isLoading) {
    return (
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div className="mx-auto max-w-[1200px]">
          {intro ? <Skeleton className="mx-auto mb-4 h-5 w-[50%]" /> : null}
          <Skeleton className="mx-auto mb-4 h-10 w-[40%]" />
          <Skeleton className="mx-auto mb-2 h-5 w-[62%]" />
          <Skeleton className="mx-auto mb-10 h-5 w-[48%]" />
          <div className="grid gap-6 md:grid-cols-3">
            {visibleShots.map((shot) => (
              <div
                key={shot.assetKey}
                className={
                  visibleShots.length === 1 ? 'md:col-start-2' : undefined
                }
              >
                <Skeleton
                  className="w-full rounded-xl"
                  style={{ aspectRatio: marketingAssets[shot.assetKey].aspect }}
                />
                <Skeleton className="mx-auto mt-3 h-4 w-[70%]" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-12 md:px-16 md:py-16">
      <div className="mx-auto max-w-[1200px]">
        {intro ? (
          <p
            className="text-foreground/70 mx-auto mb-4 max-w-[760px] text-center text-base md:text-lg"
            style={{ fontFamily: theme.fonts.body }}
          >
            {intro}
          </p>
        ) : null}
        <h2
          className="text-gradient-hero mb-4 text-center text-3xl font-bold tracking-tight md:text-4xl"
          style={{ fontFamily: theme.fonts.display }}
        >
          {title}
        </h2>
        <p
          className="text-foreground/70 mx-auto mb-10 max-w-[760px] text-center text-base md:text-lg"
          style={{ fontFamily: theme.fonts.body }}
        >
          {lead}
        </p>

        <div className="grid gap-6 md:grid-cols-3">
          {visibleShots.map((shot) => (
            <div
              key={shot.assetKey}
              data-feature-shot
              className={
                visibleShots.length === 1 ? 'md:col-start-2' : undefined
              }
            >
              <MediaSlot assetKey={shot.assetKey} />
              <p
                className="text-foreground/60 mt-3 text-center text-sm"
                style={{ fontFamily: theme.fonts.body }}
              >
                {shot.caption}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
